import { Component, HostListener, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { LucideAngularModule, MessageSquarePlus } from 'lucide-angular';
import { AuthService, CompanyContextService, I18nService, TenantService, ThemeService } from '../../core';
import { TPipe } from '../../shared/pipes';
import { TrialBadgeComponent } from '../../features/billing/components/trial-badge/trial-badge.component';
import { NotificationsDropdownComponent } from '../../features/notifications/components/notifications-dropdown/notifications-dropdown.component';
import {
  PLATFORM_FEEDBACK_NOTIFICATION_TYPE,
  type NotificationItem,
} from '../../features/notifications/models/notifications.models';
import { NotificationsStore } from '../../features/notifications/store/notifications.store';
import { PlatformFeedbackModalComponent } from '../../features/platform-feedback/components/platform-feedback-modal/platform-feedback-modal.component';
import { resolveApiAssetUrl } from '../../shared/utils/resolve-api-asset-url.util';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [LucideAngularModule, TPipe, NotificationsDropdownComponent, PlatformFeedbackModalComponent, TrialBadgeComponent],
  templateUrl: './topbar.component.html',
  styleUrl: './topbar.component.css',
})
export class TopbarComponent {
  /** Ícone inline: evita depender só do `LucideAngularModule.pick` (nome resolvido por provider). */
  readonly feedbackIconImg = MessageSquarePlus;

  private readonly auth = inject(AuthService);
  private readonly tenant = inject(TenantService);
  private readonly companyContext = inject(CompanyContextService);
  private readonly i18n = inject(I18nService);
  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeService);
  readonly notificationsStore = inject(NotificationsStore);

  readonly theme = this.themeService.theme;
  readonly isProfileOpen = signal(false);
  readonly isNotificationsOpen = signal(false);
  readonly isFeedbackOpen = signal(false);
  readonly profileName = computed(() => {
    const user = this.auth.currentUser();
    if (user?.name?.trim()) return user.name.trim();
    if (user?.email?.trim()) return user.email.trim();
    return 'Usuário';
  });
  readonly profileInitials = computed(() => {
    const value = this.profileName().trim();
    const parts = value.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return value.slice(0, 2).toUpperCase();
  });

  private readonly profileAvatarImageErrored = signal(false);

  readonly profileAvatarUrl = computed(() => {
    const raw = this.auth.currentUser()?.avatarUrl?.trim() ?? '';
    if (!raw) return '';
    const lower = raw.toLowerCase();
    if (lower.startsWith('data:image') || lower.startsWith('blob:')) return raw;
    return resolveApiAssetUrl(raw);
  });

  readonly showProfileAvatarImage = computed(
    () => !this.profileAvatarImageErrored() && this.isRenderableProfileAvatarUrl(this.profileAvatarUrl())
  );
  readonly profileMeta = computed(() => {
    const ctx = this.companyContext.activeCompany();
    const tenantCo = this.tenant.activeCompany();
    const user = this.auth.currentUser();

    const companyName = [ctx?.name, tenantCo?.name, user?.companyName]
      .map((s) => (typeof s === 'string' ? s.trim() : ''))
      .find((s) => s.length > 0);

    const roleRaw = ctx?.role ?? tenantCo?.role ?? user?.role;
    const isAdmin = this.isAdminRole(roleRaw);

    const roleLabel = this.i18n.t(
      isAdmin ? 'settings.members.roles.admin' : 'settings.members.roles.member',
      undefined,
      isAdmin ? 'Admin' : 'Member'
    );

    const displayCompany =
      companyName && companyName.toLowerCase() !== 'company'
        ? companyName
        : this.i18n.t('layout.topbar.profilecompanyfallback', undefined, 'Empresa');

    return `${displayCompany} - ${roleLabel}`;
  });

  readonly dashboardRoute = computed(() => {
    const k = this.tenant.companyKey();
    return k ? (['/c', k, 'dashboard'] as const) : (['/auth/login'] as const);
  });

  constructor() {
    effect(() => {
      this.profileAvatarUrl();
      this.profileAvatarImageErrored.set(false);
    });
  }

  @HostListener('document:visibilitychange')
  onDocumentVisibility(): void {
    if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
    if (!this.auth.isAuthenticated() || !this.tenant.companyId()) return;
    void this.notificationsStore.loadUnreadCount();
  }

  @HostListener('window:focus')
  onWindowFocus(): void {
    if (!this.auth.isAuthenticated() || !this.tenant.companyId()) return;
    void this.notificationsStore.loadUnreadCount();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isNotificationsOpen() && !this.isProfileOpen()) return;
    const target = event.target as HTMLElement | null;
    if (
      target?.closest('.notifications-wrap') ||
      target?.closest('.profile-wrap') ||
      target?.closest('app-platform-feedback-modal')
    ) {
      return;
    }
    this.isNotificationsOpen.set(false);
    this.isProfileOpen.set(false);
  }

  onProfileAvatarError(): void {
    this.profileAvatarImageErrored.set(true);
  }

  private isRenderableProfileAvatarUrl(raw: string): boolean {
    const url = raw.trim();
    if (!url) return false;
    const lower = url.toLowerCase();
    if (lower.startsWith('blob:') || lower.startsWith('data:image')) return true;
    if (lower.startsWith('https://') || lower.startsWith('http://')) return true;
    return false;
  }

  toggleFeedback(event: MouseEvent): void {
    event.stopPropagation();
    const next = !this.isFeedbackOpen();
    this.isFeedbackOpen.set(next);
    if (next) {
      this.isNotificationsOpen.set(false);
      this.isProfileOpen.set(false);
    }
  }

  onFeedbackDismissed(): void {
    this.isFeedbackOpen.set(false);
  }

  toggleNotifications(event: MouseEvent): void {
    event.stopPropagation();
    const nextOpen = !this.isNotificationsOpen();
    this.isNotificationsOpen.set(nextOpen);
    if (this.isProfileOpen()) {
      this.isProfileOpen.set(false);
    }
    if (nextOpen) {
      this.isFeedbackOpen.set(false);
      void this.notificationsStore.loadNotifications();
      void this.notificationsStore.loadUnreadCount();
    }
  }

  async onNotificationOpen(item: NotificationItem): Promise<void> {
    await this.notificationsStore.markAsRead(item.id);

    const typeNorm = (item.type || '').trim();
    const isPlatformFeedback =
      typeNorm === PLATFORM_FEEDBACK_NOTIFICATION_TYPE ||
      typeNorm.toLowerCase() === PLATFORM_FEEDBACK_NOTIFICATION_TYPE.toLowerCase();

    if (!isPlatformFeedback) return;

    const companyKey = this.tenant.companyKey();
    if (!companyKey) return;

    const cardId = this.readNotificationMetadataGuid(item.metadata, 'cardId', 'CardId');
    const workflowId = this.readNotificationMetadataGuid(item.metadata, 'workflowId', 'WorkflowId');

    if (cardId) {
      this.isNotificationsOpen.set(false);
      void this.router.navigate(['/c', companyKey, 'cards', cardId]);
      return;
    }

    if (workflowId) {
      this.isNotificationsOpen.set(false);
      void this.router.navigate(['/c', companyKey, 'workflows', workflowId]);
    }
  }

  private readNotificationMetadataGuid(
    meta: Record<string, unknown> | null,
    ...keys: string[]
  ): string | null {
    if (!meta) return null;
    for (const k of keys) {
      const v = meta[k];
      if (typeof v === 'string') {
        const t = v.trim();
        if (t) return t;
      }
    }
    return null;
  }

  async markAllNotificationsAsRead(): Promise<void> {
    await this.notificationsStore.markAllAsRead();
  }

  async retryNotifications(): Promise<void> {
    await this.notificationsStore.loadNotifications();
    await this.notificationsStore.loadUnreadCount();
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  toggleProfile(event?: MouseEvent): void {
    event?.stopPropagation();
    const next = !this.isProfileOpen();
    this.isProfileOpen.set(next);
    if (next) {
      this.isNotificationsOpen.set(false);
      this.isFeedbackOpen.set(false);
    }
  }

  goToMyData(): void {
    this.isProfileOpen.set(false);
    const k = this.tenant.companyKey();
    if (k) void this.router.navigate(['/c', k, 'profile', 'me']);
  }

  logout(): void {
    this.auth.logout();
    this.isProfileOpen.set(false);
  }

  private isAdminRole(role: string | undefined | null): boolean {
    const r = (role || '').trim().toLowerCase();
    return r === 'admin' || r === 'owner';
  }
}
